// What a bot says, and whether it says anything (AI_MANAGERS.md §6).
//
// NEVER GENERATED. A line is one its character's file holds, chosen by a draw
// and filled with the words of the moment: the team it beat, a score, the
// player it claimed. No model is asked anything, so a line costs nothing
// however many a league hears (the cost rule).
//
// TWO DRAWS, NOT ONE: whether it speaks, against its `trashTalk` dial, and
// which line. One number doing both left a quiet bot with lines it could
// never reach (AUDIT-2026-09-19 F17).
//
// IT TALKS ABOUT ITS OWN TEAM AND ABOUT WHAT HAPPENED, never about what
// anybody should do (§6.2). No line in the cast holds the words the
// notifications are kept free of, and the cast's test reads every one.

import type { StickerId } from '@fsp/domain';
import type { CastMember, Occasion } from './cast';

/**
 * The words a line for each occasion may use, beside `team` — the bot's own
 * team's name — which every occasion has. The cast's test refuses a line that
 * asks for any other, so filling one never meets a hole.
 */
export const SLOTS = {
  pick: ['player', 'round', 'overall'],
  draftDone: ['player'],
  winClose: ['opponent', 'owner', 'score', 'theirs', 'margin', 'week'],
  win: ['opponent', 'owner', 'score', 'theirs', 'margin', 'week'],
  winBig: ['opponent', 'owner', 'score', 'theirs', 'margin', 'week'],
  lossClose: ['opponent', 'owner', 'score', 'theirs', 'margin', 'week'],
  loss: ['opponent', 'owner', 'score', 'theirs', 'margin', 'week'],
  lossBig: ['opponent', 'owner', 'score', 'theirs', 'margin', 'week'],
  waiverWon: ['player'],
  tradeOffer: ['give', 'get'],
  tradeDone: ['got', 'gave', 'other'],
  champion: [],
  eliminated: [],
  // THE MATCHUP'S LINE (§6.5). Before the games, nothing about the other
  // side's players, so no line can read as a remark on their lineup.
  preview: ['opponent', 'owner', 'week'],
  leading: ['opponent', 'owner', 'score', 'theirs', 'margin', 'week'],
  trailing: ['opponent', 'owner', 'score', 'theirs', 'margin', 'week'],
} as const satisfies Record<Occasion, readonly string[]>;

/** The words a line for `O` is filled with. */
export type Words<O extends Occasion> = {
  [K in (typeof SLOTS)[O][number] | 'team']: string | number;
};

/** Two draws in [0, 1): `random` against the dial, `line` for which line. */
export interface TalkDraws {
  random: number;
  line: number;
}

/** A line with its words put in. A word it asks for and is not given is a bug, and throws. */
export function fill(line: string, words: Readonly<Record<string, string | number>>): string {
  return line.replace(/\{(\w+)\}/g, (_, key: string) => {
    const word = words[key];
    if (word === undefined)
      throw new Error(`a bot's line asks for {${key}}, which it was not given`);
    return String(word);
  });
}

/** A line as a pattern: its words as written, and anything where a word goes. */
export function templateOf(line: string): RegExp {
  const parts = line.split(/\{\w+\}/).map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return new RegExp(`^${parts.join('.+?')}$`);
}

/**
 * What `member` says on this occasion, or null when its dial keeps it quiet.
 * At the dial it speaks: the gate is `>`.
 *
 * NOT WHAT IT SAID LATELY. Given the lines it said last (`recent`, filled),
 * it passes over one of them for the next in its pool: "the whole roster is
 * under review" two weeks running reads as a bot with one sentence. When every
 * line is recent it says the one drawn.
 */
export function say<O extends Occasion>(
  member: CastMember,
  occasion: O,
  words: Words<O>,
  draws: TalkDraws,
  recent: readonly string[] = [],
  /** The seat's own talkativeness, where a commissioner set it (B-462); the file's otherwise. */
  trashTalk: number = member.dials.trashTalk,
): string | null {
  if (draws.random > trashTalk) return null;
  const pool = member.lines[occasion];
  const drawn = Math.floor(draws.line * pool.length) % pool.length;
  for (let k = 0; k < pool.length; k += 1) {
    const line = pool[(drawn + k) % pool.length]!;
    const pattern = templateOf(line);
    if (!recent.some((r) => pattern.test(r))) return fill(line, words);
  }
  return fill(pool[drawn]!, words);
}

/**
 * Whether a week is worth a line (DIRECTION A4): decided by under five points
 * or by thirty or more. An ordinary week goes unremarked.
 */
export function isStory(occasion: ResultOccasion | null): occasion is ResultOccasion {
  return (
    occasion === 'winClose' ||
    occasion === 'lossClose' ||
    occasion === 'winBig' ||
    occasion === 'lossBig'
  );
}

/** The six ways a week can go, each with lines of its own. */
export type ResultOccasion = 'winClose' | 'win' | 'winBig' | 'lossClose' | 'loss' | 'lossBig';

/**
 * Which result a week was, told as a person would tell it: by the margin. A
 * one-point win and a fifty-point win are different weeks, and the cast says
 * so. Under five points is close; thirty or more is big. A tie is neither, and
 * nobody says anything about it.
 */
export function resultOccasion(score: number, theirs: number): ResultOccasion | null {
  // IN TENTHS, as the margin is shown: 64.1 less 59.1 is 4.999… in floating
  // point, and a five-point win must not read as a close one.
  const margin = Math.round(Math.abs(score - theirs) * 10) / 10;
  if (margin === 0) return null;
  const won = score > theirs;
  if (margin < 5) return won ? 'winClose' : 'lossClose';
  if (margin >= 30) return won ? 'winBig' : 'lossBig';
  return won ? 'win' : 'loss';
}

/**
 * The sticker a bot follows its line with, sometimes (PLAN-2026-10-30-stickers
 * §6): one that fits what the line is about — a big win's *Boom!*, a close
 * loss's *Ouch.* — when the seat's talkativeness lets it (`sticker`, a draw
 * keyed by the moment, at or under the dial), so The Accountant almost never
 * sends one and Chaos Gremlin nearly always does. Null for a moment with no
 * sticker that fits it.
 */
export function botSticker(
  occasion: Occasion,
  trashTalk: number,
  draws: { sticker: number; which: number },
): StickerId | null {
  const fits = STICKER_FOR[occasion] ?? [];
  if (fits.length === 0 || draws.sticker > trashTalk) return null;
  return fits[Math.min(fits.length - 1, Math.floor(draws.which * fits.length))]!;
}

const STICKER_FOR: Partial<Record<Occasion, readonly StickerId[]>> = {
  winBig: ['boom', 'on-fire'],
  winClose: ['phew'],
  lossClose: ['ouch'],
  lossBig: ['rough-week'],
  champion: ['lets-go'],
  eliminated: ['good-game'],
};
